import { apiClient } from '../config';
import { ApiResponse } from '../types';

export interface Tag {
  id: string;
  name: string;
  category: string;
  color: string;
  description: string | null;
  sort_order: number;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export interface TagsResponse {
  tags: Tag[];
  grouped: Record<string, Tag[]>;
  categories: string[];
}

export interface TagSuggestion extends Tag {
  reason?: string;
}

export class TagService {
  // Get all tags, optionally filtered by category
  static async getTags(category?: string): Promise<TagsResponse> {
    const params: Record<string, string> = {};
    if (category) params.category = category;

    const response = await apiClient.get<ApiResponse<TagsResponse>>('/api/tags', { params });
    return response.data.data;
  }

  // Get tag categories
  static async getCategories(): Promise<string[]> {
    const response = await apiClient.get<ApiResponse<string[]>>('/api/tags/categories');
    return response.data.data;
  }

  // Create a new tag
  static async createTag(data: {
    name: string;
    category: string;
    color?: string;
    description?: string;
  }): Promise<Tag> {
    const response = await apiClient.post<ApiResponse<Tag>>('/api/tags', data);
    return response.data.data;
  }

  // Update a tag
  static async updateTag(
    id: string,
    data: Partial<{
      name: string;
      color: string;
      description: string;
      sort_order: number;
      is_active: boolean;
    }>
  ): Promise<Tag> {
    const response = await apiClient.put<ApiResponse<Tag>>(`/api/tags/${id}`, data);
    return response.data.data;
  }

  // Delete a tag
  static async deleteTag(id: string): Promise<void> {
    await apiClient.delete(`/api/tags/${id}`);
  }

  // Get tags for a content item
  static async getContentTags(contentId: string): Promise<Tag[]> {
    const response = await apiClient.get<ApiResponse<Tag[]>>(`/api/tags/content/${contentId}`);
    return response.data.data;
  }

  // Add tags to content
  static async addTagsToContent(
    contentId: string,
    tagIds: string[]
  ): Promise<{ added: number; skipped: number; tags: Tag[] }> {
    const response = await apiClient.post<
      ApiResponse<{ added: number; skipped: number; tags: Tag[] }>
    >(`/api/tags/content/${contentId}`, { tag_ids: tagIds });
    return response.data.data;
  }

  // Remove a tag from content
  static async removeTagFromContent(contentId: string, tagId: string): Promise<void> {
    await apiClient.delete(`/api/tags/content/${contentId}/${tagId}`);
  }

  // Bulk add tags to multiple content items
  static async bulkAddTags(
    contentIds: string[],
    tagIds: string[]
  ): Promise<{ added: number; skipped: number }> {
    const response = await apiClient.post<ApiResponse<{ added: number; skipped: number }>>(
      '/api/tags/content/bulk',
      { content_ids: contentIds, tag_ids: tagIds }
    );
    return response.data.data;
  }

  // Bulk remove tags from multiple content items
  static async bulkRemoveTags(
    contentIds: string[],
    tagIds: string[]
  ): Promise<{ removed: number }> {
    const response = await apiClient.delete<ApiResponse<{ removed: number }>>(
      '/api/tags/content/bulk',
      { data: { content_ids: contentIds, tag_ids: tagIds } }
    );
    return response.data.data;
  }

  // Get auto-tag suggestions based on content metadata
  static async getSuggestions(contentId: string): Promise<Tag[]> {
    const response = await apiClient.get<ApiResponse<Tag[]>>(
      `/api/tags/suggestions/${contentId}`
    );
    return response.data.data;
  }
}
